import { useEffect, useRef, useState } from 'react'
import { Icon } from './Layout'

function decodeAdMetadata(metadataUri) {
  try {
    return JSON.parse(atob(metadataUri?.split(',')[1] ?? '')) ?? {}
  } catch {
    return {}
  }
}

const AVATAR_COLORS = [
  '#FFD23F', '#5B8DEF', '#FF6B6B', '#4ECDC4', '#A78BFA', '#FF9F45',
]
function advertiserInitial(title) {
  return (title?.trim()?.[0] || '?').toUpperCase()
}
function advertiserColor(seed) {
  let hash = 0
  for (let i = 0; i < (seed?.length ?? 0); i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

function hostnameFromUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url || ''
  }
}

export default function AdCard({ ad, onView, onClick }) {
  const { title, url, description, banner } = decodeAdMetadata(ad.metadataUri)
  const initial = advertiserInitial(title)
  const color = advertiserColor(title || url || String(ad.id))
  const host = hostnameFromUrl(url)

  const [views, setViews] = useState(ad.views ?? 0)
  const hasFiredView = useRef(false)
  const cardRef = useRef(null)

  useEffect(() => {
    if (!cardRef.current || hasFiredView.current) return
    const node = cardRef.current
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting && !hasFiredView.current) {
          hasFiredView.current = true
          setViews((v) => v + 1)
          onView?.(ad.id)
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [ad.id, onView])

  const handleVisit = () => {
    if (!url) return
    onClick?.(ad.id)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <article
      ref={cardRef}
      className="bg-surface-container-low neo-border neo-shadow p-3 lg:p-6 relative border-dashed lg:border-solid"
    >
      <div className="flex justify-between items-start gap-3 mb-3 lg:mb-4">
        <div className="flex gap-2 lg:gap-4 min-w-0">
          <div
            className="w-9 h-9 lg:w-12 lg:h-12 border border-on-background/20 lg:neo-border shrink-0 flex items-center justify-center font-extrabold text-on-background/80 text-sm lg:text-lg"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          >
            {initial}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-on-background text-sm lg:text-base truncate">
              {title || 'Sponsored project'}
            </p>
            {host && (
              <span className="text-[11px] text-on-surface-variant font-mono truncate">{host}</span>
            )}
          </div>
        </div>
        <div className="px-2 lg:px-3 py-0.5 lg:py-1 bg-primary-container border border-on-background/20 lg:neo-border flex items-center gap-1 shrink-0">
          <Icon name="campaign" filled className="text-[12px] lg:text-[14px] text-on-primary-fixed" />
          <span className="text-[10px] lg:text-[11px] font-bold text-on-primary-fixed uppercase tracking-wider">
            Sponsored
          </span>
        </div>
      </div>

      {/* Banner — full width, the focal point of an ad card */}
      {banner && (
        <button
          type="button"
          onClick={handleVisit}
          className="block w-full mb-3 lg:mb-4 group focus:outline-none"
          aria-label={`Visit ${title || 'sponsor'}`}
        >
          <img
            src={banner}
            alt={title || 'Sponsored banner'}
            className="w-full h-44 lg:h-64 object-cover border border-on-background/20 lg:neo-border group-hover:brightness-95 transition-all"
          />
        </button>
      )}

      {/* Pitch text + CTA */}
      <div className="flex items-start justify-between gap-3 mb-3 lg:mb-4">
        <p className="text-sm lg:text-body-lg font-body-lg leading-relaxed text-on-background flex-1 min-w-0">
          {description}
        </p>
        <button
          type="button"
          onClick={handleVisit}
          className="shrink-0 px-4 py-2 bg-on-background text-surface font-bold text-[11px] lg:text-[12px] uppercase tracking-widest border-2 border-on-background hover:opacity-90 transition-opacity"
        >
          Visit ➜
        </button>
      </div>

      {/* Action bar — eye icon replaces reshare in this exact slot, no like/comment */}
      <div className="flex justify-end pt-3 lg:pt-4 border-t border-on-background/10">
        <div className="flex items-center gap-1.5 text-on-surface-variant" title="Views">
          <Icon name="visibility" className="text-[18px] lg:text-[24px]" />
          <span className="text-[11px] lg:text-[12px]">
            {views >= 1000 ? `${(views / 1000).toFixed(1)}k` : views || ''}
          </span>
        </div>
      </div>
    </article>
  )
}